import { Family, Tier, FAMILY_LABELS, STANDARD_FAMILIES, TIER_NAMES } from '../config';
import { ELEMENT_DEFS, ElementDef } from '../data/elements';

/**
 * A hint pointing at the next reachable discovery for the player. Produced by
 * `computeNextDiscoveryHint` and consumed by the Hub teaser.
 *
 * `prevTier` is the highest already-discovered tier in `family`. `currTier` is
 * `prevTier + 1` — the tier that would unlock next on a merge. Fields are
 * omitted (null return) when every element is already discovered.
 */
export interface NextDiscoveryHint {
  family: Family;
  familyLabel: string;
  prevTier: Tier;
  prevTierName: string;
  currTier: Tier;
  currTierName: string;
  currElementName: string;
}

/**
 * Entry in the codex for a specific element.
 */
export interface CodexEntry {
  defId: string;
  family: Family;
  tier: Tier;
  name: string;
  lore: string;
  discovered: boolean;
  discoveredAt: number | null; // timestamp
}

/**
 * Family completion stats.
 */
export interface FamilyStats {
  family: Family;
  label: string;
  discovered: number;
  total: number;
  percent: number;
}

/**
 * CodexManager tracks all element discoveries and provides
 * query methods for the Codex UI.
 */
export class CodexManager {
  private entries: Map<string, CodexEntry> = new Map();

  constructor() {
    // Initialize from ELEMENT_DEFS
    for (const def of ELEMENT_DEFS) {
      this.entries.set(def.id, {
        defId: def.id,
        family: def.family,
        tier: def.tier,
        name: def.name,
        lore: def.lore,
        discovered: false,
        discoveredAt: null,
      });
    }
  }

  /**
   * Mark an element as discovered. Returns true if it was a new discovery.
   */
  discover(defId: string): boolean {
    const entry = this.entries.get(defId);
    if (!entry) return false;
    if (entry.discovered) return false;

    entry.discovered = true;
    entry.discoveredAt = Date.now();
    return true;
  }

  /**
   * Check if an element has been discovered.
   */
  isDiscovered(defId: string): boolean {
    return this.entries.get(defId)?.discovered ?? false;
  }

  /**
   * Get a specific codex entry.
   */
  getEntry(defId: string): CodexEntry | undefined {
    return this.entries.get(defId);
  }

  /**
   * Get all entries for a family, sorted by tier.
   */
  getByFamily(family: Family): CodexEntry[] {
    return Array.from(this.entries.values())
      .filter(e => e.family === family)
      .sort((a, b) => a.tier - b.tier);
  }

  /**
   * Get all entries for a tier, sorted by family.
   */
  getByTier(tier: Tier): CodexEntry[] {
    return Array.from(this.entries.values())
      .filter(e => e.tier === tier);
  }

  /**
   * Get completion stats per family.
   */
  getFamilyStats(): FamilyStats[] {
    const families = Object.values(Family);
    return families.map(family => {
      const entries = this.getByFamily(family);
      const discovered = entries.filter(e => e.discovered).length;
      return {
        family,
        label: FAMILY_LABELS[family],
        discovered,
        total: entries.length,
        percent: entries.length > 0 ? Math.round((discovered / entries.length) * 100) : 0,
      };
    });
  }

  /**
   * Get overall completion stats.
   */
  getTotalStats(): { discovered: number; total: number; percent: number } {
    const all = Array.from(this.entries.values());
    const discovered = all.filter(e => e.discovered).length;
    return {
      discovered,
      total: all.length,
      percent: all.length > 0 ? Math.round((discovered / all.length) * 100) : 0,
    };
  }

  /**
   * Get recent discoveries sorted by discovery time (newest first).
   */
  getRecentDiscoveries(limit: number = 10): CodexEntry[] {
    return Array.from(this.entries.values())
      .filter(e => e.discovered && e.discoveredAt !== null)
      .sort((a, b) => (b.discoveredAt ?? 0) - (a.discoveredAt ?? 0))
      .slice(0, limit);
  }

  /**
   * Get all undiscovered elements (for hints/teasers).
   */
  getUndiscovered(): CodexEntry[] {
    return Array.from(this.entries.values())
      .filter(e => !e.discovered);
  }

  /**
   * Bulk-load discovery state (for save/load).
   */
  loadDiscoveries(discoveredIds: string[]): void {
    for (const id of discoveredIds) {
      const entry = this.entries.get(id);
      if (entry) {
        entry.discovered = true;
        // Don't overwrite timestamp if already set
        if (entry.discoveredAt === null) {
          entry.discoveredAt = Date.now();
        }
      }
    }
  }

  /**
   * Export all discovered element IDs (for save).
   */
  exportDiscoveries(): string[] {
    return Array.from(this.entries.values())
      .filter(e => e.discovered)
      .map(e => e.defId);
  }

  /**
   * Compute the next discovery hint for the Hub teaser.
   *
   * Walks every standard family and returns the lowest-tier undiscovered
   * element whose previous tier IS already discovered — i.e. the player is
   * one merge away from it. If all 56 standard-family elements are
   * discovered, or no families have any progress yet, returns a starter hint
   * pointing at the first undiscovered Fragment so the UI is never blank.
   *
   * Wild is intentionally excluded — it's an off-ladder element.
   */
  computeNextDiscoveryHint(): NextDiscoveryHint | null {
    // Preferred: find the lowest (family, tier) where tier > 1, !discovered,
    // and tier-1 is discovered. That's a discovery the player can actually
    // reach with a single merge.
    for (let t = Tier.Shard; t <= Tier.Eternal; t++) {
      for (const family of STANDARD_FAMILIES) {
        const curr = this.getByFamilyAndTier(family, t as Tier);
        if (!curr || curr.discovered) continue;
        const prev = this.getByFamilyAndTier(family, (t - 1) as Tier);
        if (!prev || !prev.discovered) continue;
        return this.buildHint(family, (t - 1) as Tier, t as Tier);
      }
    }

    // Fallback: nothing chainable — point at the first undiscovered Fragment
    // so the hub teaser still has something to aim at.
    for (const family of STANDARD_FAMILIES) {
      const t1 = this.getByFamilyAndTier(family, Tier.Fragment);
      if (t1 && !t1.discovered) {
        return this.buildHint(family, Tier.Fragment, Tier.Fragment);
      }
    }

    // Everything discovered across standard families.
    return null;
  }

  /**
   * Mark an element as discovered, returning whether it was new.
   *
   * Thin alias around `discover()` — exists so callers reading
   * `codex.markDiscovered(id)` get an obvious boolean instead of having to
   * remember which side of the `discover` name means "first time".
   */
  markDiscovered(defId: string): boolean {
    return this.discover(defId);
  }

  /** O(1) lookup by (family, tier) — used by computeNextDiscoveryHint. */
  private getByFamilyAndTier(family: Family, tier: Tier): CodexEntry | undefined {
    // Entries map is keyed by defId; iterate within the one family (7 items).
    for (const entry of this.entries.values()) {
      if (entry.family === family && entry.tier === tier) return entry;
    }
    return undefined;
  }

  private buildHint(family: Family, prevTier: Tier, currTier: Tier): NextDiscoveryHint {
    const curr = this.getByFamilyAndTier(family, currTier);
    return {
      family,
      familyLabel: FAMILY_LABELS[family],
      prevTier,
      prevTierName: TIER_NAMES[prevTier],
      currTier,
      currTierName: TIER_NAMES[currTier],
      currElementName: curr?.name ?? '???',
    };
  }
}
